import React from 'react';
import { GatsbyImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import {
  breakpointSizes,
  minBreakpointQuery,
  sectionMargins,
  standardColours,
  fontWeights,
  fluidFontSize,
  zIndexLayers,
  standardTransition,
} from '../styles';
import { Container, Link } from './ui';
import ContentBlocks from './ContentBlocks';

const StyledContentBlocksImageLinks = styled.section`
  position: relative;
  ${sectionMargins('50px', '100px')};
  background: linear-gradient(
    to bottom,
    ${standardColours.lightGrey} calc(100% - 150px),
    ${standardColours.white} calc(100% - 150px)
  );

  &:before {
    ${minBreakpointQuery.xxlarge`
      content: '';
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      width: 80px;
      background-color: ${standardColours.white};
    `}

    ${minBreakpointQuery.xxxlarge`
      width: 100px;
    `}
  }
`;

const StyledInner = styled.div`
  position: relative;
`;

const StyledContainer = styled(Container)`
  padding: 0;
`;

const StyledImageLinks = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 20px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;
  overflow-x: scroll;

  ${minBreakpointQuery.small`
    gap: 30px;
  `}

  ${minBreakpointQuery.large`
    gap: 40px;
  `}
`;

// moved up for hover
const StyledText = styled.span`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  margin-top: auto;
  padding: 30px 30px 40px 30px;
  color: ${standardColours.white};
  font-weight: ${fontWeights.semibold};
  ${fluidFontSize(
    '18px',
    '28px',
    breakpointSizes.tiny,
    breakpointSizes.xxxxlarge
  )};
  line-height: 1.1;
  background: linear-gradient(
    to bottom,
    ${standardColours.transparent} 0%,
    ${standardColours.black} 100%
  );
  transform: translateY(10px);
  transition: ${standardTransition('transform')};
`;

const StyledImageLink = styled(Link)`
  position: relative;
  display: flex;
  flex-direction: column;
  min-width: 260px;
  scroll-snap-align: start;
  z-index: ${zIndexLayers.first};
  overflow: hidden;

  ${minBreakpointQuery.small`
    min-width: 280px;
  `}

  ${minBreakpointQuery.medium`
    min-width: 300px;
  `}  

  ${minBreakpointQuery.large`
    min-width: 320px;
  `}

  ${minBreakpointQuery.xxlarge`
    min-width: 340px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    min-width: 360px;
  `}

  &:first-child {
    margin-left: 0;
  }

  &:hover {
    ${StyledText} {
      transform: translateY(0);
    }
  }
`;

const StyledImage = styled(GatsbyImage)``;

const StyledIcon = styled.img`
  margin-right: 12px;
  height: 35px;
  width: 35px;

  ${minBreakpointQuery.small`
    height: 40px;
    width: 40px;
  `}

  ${minBreakpointQuery.medium`
    margin-right: 16px;
    height: 45px;
    width: 45px;
  `}  

  ${minBreakpointQuery.large`
    height: 50px;
    width: 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-right: 20px;
    height: 55px;
    width: 55px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    height: 60px;
    width: 60px;
  `}
`;

const ContentBlocksImageLinks = ({ heading, contentBlocks, imageLinks }) => (
  <StyledContentBlocksImageLinks>
    <StyledInner>
      <ContentBlocks
        heading={heading}
        items={contentBlocks}
        isContentBlocksImageLinks={true}
      />
      <StyledContainer wide={true}>
        <StyledImageLinks>
          {imageLinks.map(({ image, icon, text, link }, id) => (
            <StyledImageLink
              key={id}
              {...(link.url
                ? {
                    href: link.url,
                    target: '_blank',
                    rel: 'noopener noreferrer',
                  }
                : {
                    to: link.page,
                  })}
            >
              <StyledImage image={image.gatsbyImageData} alt={image.alt} />
              <StyledText>
                {icon && (
                  <StyledIcon src={icon.url} alt={icon.alt} loading="lazy" />
                )}
                {text}
              </StyledText>
            </StyledImageLink>
          ))}
        </StyledImageLinks>
      </StyledContainer>
    </StyledInner>
  </StyledContentBlocksImageLinks>
);

export default ContentBlocksImageLinks;
